import { useCallback, useEffect, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import { useAnalytics } from '../../../providers/AnalyticsProvider/AnalyticsProvider';
import DumbSearchBlock from './SearchBlock.dumb';

/* ---------------------------------- Types --------------------------------- */

export interface SearchBlockI {
    /** Method for opening and shutting the search form */
    setSearchOpen: (state: boolean) => void;

    /** Boolean representing the search form state, open or shut */
    searchOpen: boolean;
}

/* -------------------------------- Component ------------------------------- */

const SearchBlock = ({ setSearchOpen, searchOpen }: SearchBlockI) => {
    const navigate = useNavigate();
    const { pageObject } = useAnalytics();

    const location = useLocation();
    // Structure of the state passed by router, set in performSearch()
    const state = location.state as {
        from?: string;
    };

    // Checking to see if on the search page
    const searchMatch = useMatch({ path: '/search', end: false });
    const isOnSearchPage = Boolean(searchMatch);

    // Grab the query if that exists
    const queryMatch = useMatch('/search/:query');
    const query = queryMatch?.params?.query || '';

    const [searchTerm, setSearchTerm] = useState(query);

    const performSearch = useCallback(
        (value: string) => {
            // Only update the returnUrl if we are not on the search page
            // That way you can make multiple searches and go back to the page before you started searching
            const returnUrl = !isOnSearchPage
                ? location.pathname
                : state?.from ?? '/';

            if (pageObject) {
                window.analytics.trigger({
                    currentPage: pageObject,
                    type: 'search.submitted',
                    query: searchTerm,
                });
            }

            navigate(`/search/${value}`, {
                state: { from: returnUrl },
            });
        },
        [
            location.pathname,
            navigate,
            isOnSearchPage,
            state?.from,
            pageObject,
            searchTerm,
        ],
    );

    const closeSearch = () => {
        // Empty search term to prevent another search happening
        setSearchTerm('');

        // Redirect if there is somewhere to navigate back to
        if (state?.from) {
            navigate(state.from);
        }
        // If not on the search page then the search bar should close
        if (!isOnSearchPage) {
            setSearchOpen(false);
        }
    };

    useEffect(() => {
        if (!isOnSearchPage) {
            setSearchOpen(false);
        }
    }, [setSearchOpen, isOnSearchPage]);

    useEffect(() => {
        if (searchTerm) {
            performSearch(searchTerm);
        }

        return () => {
            setSearchTerm('');
        };
    }, [performSearch, searchTerm]);

    /* -------------------------------- Component ------------------------------- */

    return (
        <DumbSearchBlock
            closeSearch={closeSearch}
            isOnSearchPage={isOnSearchPage}
            isOpen={searchOpen}
            openSearch={() => setSearchOpen(true)}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
        />
    );
};

/* --------------------------------- Export --------------------------------- */

export default SearchBlock;
